import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { GlobalService } from './global.service';
import { CompaniesService } from './companies.service';
import { CompaniesClass } from '../models/companies-class';
import { ProductsClass } from '../models/products-class';
import { PaymentsSplitClass } from '../models/payments-class';
import { HTML } from '../models/common-class';
import { AlertService } from './alert.service';

// Imports de componentes removidos para carregamento dinâmico (Dynamic Imports)

/**
 * DialogManagementService
 *
 * Service focado em gestão de diálogos/modals:
 * - Help button
 * - Edit/Add Companies, Products, PaymentSplit
 * - HTML Editor
 * - Privacy dialog
 */
@Injectable({
  providedIn: 'root',
})
export class DialogManagementService {
  constructor(
    public dialog: MatDialog,
    public globalService: GlobalService,
    private companiesService: CompaniesService,
    private alert: AlertService,
  ) {}

  /**
   * Abre dialog de ajuda
   * closeOnNavigation: false permite que o vídeo flutuante sobreviva à navegação
   */
  async helpbutton(id: number) {
    const { HelpComponent } = await import('src/app/shared/components/help/help.component');
    let dialogRef: any;
    if (this.globalService.isMobileDevice) {
      dialogRef = this.dialog.open(HelpComponent, {
        data: id,
        maxWidth: '100vw',
        height: '100%',
        width: '100%',
        closeOnNavigation: false,
      });
    } else {
      dialogRef = this.dialog.open(HelpComponent, {
        data: id,
        closeOnNavigation: false,
        maxWidth: '600px',
        width: '100%',
      });
    }

    dialogRef.afterClosed().subscribe(() => {});
  }

  /**
   * Editar companhia
   */
  async editCompanie(companiesClass: CompaniesClass) {
    window.history.pushState(null, null, window.location.href);
    const { DialogcompaniemanagementComponent } =
      await import('src/app/pages/sitemanagement/companiemanagement/dialogcompaniemanagement/dialogcompaniemanagement.component');
    let dialogRef: any;
    // Sem scroll interno: o dialog cresce com o conteúdo e usa apenas o scroll geral do site
    if (this.globalService.isMobileDevice) {
      dialogRef = this.dialog.open(DialogcompaniemanagementComponent, {
        data: companiesClass,
        maxWidth: '100vw',
        maxHeight: 'none',
        width: '100%',
        panelClass: 'dc-dialog-panel',
      });
    } else {
      dialogRef = this.dialog.open(DialogcompaniemanagementComponent, {
        data: companiesClass,
        width: '900px',
        maxWidth: '95vw',
        maxHeight: 'none',
        panelClass: 'dc-dialog-panel',
      });
    }

    dialogRef.autoFocus = true;
    dialogRef.disableClose = true;
    const dados = await firstValueFrom(dialogRef.afterClosed());
    this.companiesService.GetCompaniesAll({ forceRefresh: true }).subscribe({
      next: d => (this.globalService.companies = d),
      error: error => {
        this.alert.showRequestFailed();
        console.log(error);
      },
    });
    return dados;
  }

  /**
   * Editar produto
   */
  async editProduct(productsClass: ProductsClass) {
    window.history.pushState(null, null, window.location.href);

    const { DialogproductmanagementComponent } =
      await import('src/app/shared/components/products/productsmanagement/dialogproductmanagement/dialogproductmanagement.component');
    let dialogRef: any;
    if (this.globalService.isMobileDevice) {
      dialogRef = this.dialog.open(DialogproductmanagementComponent, {
        data: productsClass,
        maxWidth: '100vw',
        maxHeight: '100vh',
        height: '100%',
        width: '100%',
      });
    } else {
      dialogRef = this.dialog.open(DialogproductmanagementComponent, {
        data: productsClass,
      });
    }

    dialogRef.autoFocus = true;
    dialogRef.disableClose = true;
    dialogRef.afterClosed().subscribe();
  }

  /**
   * Adicionar novo produto
   */
  async addProduct() {
    let productsClass: ProductsClass;
    productsClass = new ProductsClass();
    // ... (logic omitted for brevity but identical)
    productsClass.idCompanie = null;
    productsClass.idProduct = null;
    productsClass.ProductsTitle = '';
    productsClass.Name = '';
    productsClass.ShortName = '';
    productsClass.Description = '';
    productsClass.comments = '';
    productsClass.Icon = null;
    productsClass.IconDestaque = null;
    productsClass.IconMaxWidth = 50;
    productsClass.IconMinWidth = 50;
    productsClass.IconMaxHeight = 50;
    productsClass.IconMinHeight = 50;
    productsClass.IconURL = '';
    productsClass.TextColor = '#ffffff';
    productsClass.BackgroundColor = '#f2a053';
    productsClass.DateStart = new Date();
    productsClass.DateEnd = null;
    productsClass.ProdutoInativo = true;
    productsClass.Order = this.globalService.productsClass?.length + 1;
    productsClass.OrderOptions = this.globalService.productsClass?.length + 1;
    productsClass.IsForSale = true;
    productsClass.VisibleOptions = true;
    productsClass.File = null;
    productsClass.FileURL = '';
    productsClass.ShowMatricula = false;
    productsClass.ShowCapital = false;
    productsClass.ShowSalario = false;
    productsClass.isProduct = true;
    productsClass.ShowCarInspection = false;
    productsClass.ShowInsuranceClaimsCausesTypes = true;
    productsClass.UUID = this.globalService.loggedUserClass.UUID;
    productsClass.productsTypesClass = [];
    productsClass.productsClaimsCompaniesClass = [];
    productsClass.UUIDLastChange = this.globalService.loggedUserClass.UUID;
    productsClass.idProductsTypes = null;

    window.history.pushState(null, null, window.location.href);
    const { DialogproductmanagementComponent } =
      await import('src/app/shared/components/products/productsmanagement/dialogproductmanagement/dialogproductmanagement.component');
    let dialogRef: any;
    if (this.globalService.isMobileDevice) {
      dialogRef = this.dialog.open(DialogproductmanagementComponent, {
        data: productsClass,
        maxWidth: '100vw',
        maxHeight: '100vh',
        height: '100%',
        width: '100%',
      });
    } else {
      dialogRef = this.dialog.open(DialogproductmanagementComponent, {
        data: productsClass,
      });
    }

    dialogRef.autoFocus = true;
    dialogRef.disableClose = true;
    dialogRef.afterClosed().subscribe(result => {
      console.log(result);
    });
  }

  /**
   * Editar payment split
   */
  async editPaymentSplit(paymentsSplitClass: PaymentsSplitClass) {
    window.history.pushState(null, null, window.location.href);
    const { DialogpaymentssplitmanagementComponent } =
      await import('src/app/pages/sitemanagement/paymentssplitmanagement/dialogpaymentssplitmanagement/dialogpaymentssplitmanagement.component');
    let dialogRef: any;
    if (this.globalService.isMobileDevice) {
      dialogRef = this.dialog.open(DialogpaymentssplitmanagementComponent, {
        data: paymentsSplitClass,
        maxWidth: '100vw',
        maxHeight: '100vh',
        height: '100%',
        width: '100%',
      });
    } else {
      dialogRef = this.dialog.open(DialogpaymentssplitmanagementComponent, {
        data: paymentsSplitClass,
      });
    }
    dialogRef.autoFocus = true;
    dialogRef.disableClose = true;
    dialogRef.afterClosed().subscribe(() => {});
  }

  /**
   * Adicionar payment split
   */
  async addPaymentSplit() {
    let paymentsSplitClass: PaymentsSplitClass;
    paymentsSplitClass = new PaymentsSplitClass();
    window.history.pushState(null, null, window.location.href);
    const { DialogpaymentssplitmanagementComponent } =
      await import('src/app/pages/sitemanagement/paymentssplitmanagement/dialogpaymentssplitmanagement/dialogpaymentssplitmanagement.component');
    let dialogRef: any;
    if (this.globalService.isMobileDevice) {
      dialogRef = this.dialog.open(DialogpaymentssplitmanagementComponent, {
        data: paymentsSplitClass,
        maxWidth: '100vw',
        maxHeight: '100vh',
        height: '100%',
        width: '100%',
      });
    } else {
      dialogRef = this.dialog.open(DialogpaymentssplitmanagementComponent, {
        data: paymentsSplitClass,
      });
    }

    dialogRef.autoFocus = true;
    dialogRef.disableClose = true;
    dialogRef.afterClosed().subscribe((_result: any) => {});
  }

  /**
   * Editor de HTML/texto rico
   */
  async editHTMLText(texto: string): Promise<any> {
    window.history.pushState(null, null, window.location.href);
    const { HtmlComponent } = await import('../components/html/html.component');
    let dialogRef: any;
    if (this.globalService.isMobileDevice) {
      dialogRef = this.dialog.open(HtmlComponent, {
        data: texto,
        maxWidth: '100vw',
        height: '100%',
        width: '100%',
      });
    } else {
      dialogRef = this.dialog.open(HtmlComponent, {
        data: texto,
        maxWidth: '100vw',
        height: '80%',
        width: '100%',
      });
    }

    dialogRef.disableClose = false;

    dialogRef.afterClosed().subscribe((result: HTML) => {
      console.log(result);
      if (result.isConfirmed) {
        return (texto = result.HTMLText);
      }
    });
  }

  /**
   * Dialog de privacidade/políticas
   */
  async openDialogPrivacity(id: number) {
    const { PrivacyComponent } = await import('src/app/pages/privacy/privacy.component');
    if (this.globalService.isMobileDevice) {
      const dialogRef = this.dialog.open(PrivacyComponent, {
        data: id,
        maxWidth: '100vw',
        height: '100%',
        width: '100%',
      });
      dialogRef.afterClosed().subscribe(() => {});
    } else {
      const dialogRef = this.dialog.open(PrivacyComponent, {
        data: id,
      });
      dialogRef.afterClosed().subscribe(() => {});
    }
  }
}
